/**
 * Adapter de "resource" service-backed para o componente `MasterDetail`.
 *
 * O `MasterDetail` (componente genérico compartilhado) consome
 * `customResource ?? apiResource(resourceUrl)`. O `apiResource` do mixin global
 * (a) faz as chamadas HTTP inline — fora da camada `src/api/ativacaoped` — e
 * (b) notifica erros automaticamente. Este factory devolve um objeto com a
 * MESMA superfície do `apiResource` (`get`, `getSilence`, `save`, `silentSave`,
 * `delete`, `patch`), mas delegando cada verbo a um método da camada de
 * services (que são silenciosos).
 *
 * Contrato (idêntico ao `apiResource` do mixin):
 * - sucesso              → `resolve(payload)` (o payload do service, sem envelope extra);
 * - corpo `{ error }`    → `notify(payload)` + `reject(payload)` (legado 200 + error);
 * - exceção (axios/erro) → `notify(error)` + `reject(error)`;
 * - verbo não fornecido  → `notify({ error })` + reject (em vez de TypeError opaco).
 *
 * Observações importantes:
 * - `notify` é o **notificador de ERRO** (normalmente `this.notify` do mixin).
 *   O adapter NUNCA emite toast de sucesso/info — quem faz isso é o
 *   `MasterDetail` (após `save`/`delete`) ou a própria tela (lotes/importações).
 * - O adapter NÃO emite overlay próprio: o overlay vem do `withLoading` de cada
 *   service (chave única por service). Consequência: `delete`/`patch` — que no
 *   `apiResource` não tinham overlay — passam a exibir o overlay global.
 * - `getSilence`/`silentSave` caem para `get`/`save` quando não informados
 *   (o service continua aplicando o overlay do `withLoading`).
 * - Verbos extras (ex.: `batchDelete`, `batchSave`) podem ser acrescentados pela
 *   tela: `{ ...createServiceResource({...}), batchDelete }`.
 *
 * @typedef {Object} ServiceResourceHandlers
 * @property {(error: any) => void} notify - Notificador de ERRO (this.notify do mixin)
 * @property {(param: {id?: any, query?: any}) => Promise<any>} [get]
 * @property {(param: {id?: any, query?: any}) => Promise<any>} [getSilence]
 * @property {(data: any, id?: any) => Promise<any>} [save]
 * @property {(data: any, id?: any) => Promise<any>} [silentSave]
 * @property {(id: any) => Promise<any>} [remove]
 * @property {(data: any, id: any) => Promise<any>} [patch]
 */

/**
 * Normaliza o parâmetro do `get`, replicando o `apiResource`:
 * `get(1)` → `{ id: 1 }`; `get({ id, query })` → o próprio objeto.
 *
 * @param {number|string|Object} [param] - ID ou `{ id, query }`
 * @returns {{id: any, query: any}} Parâmetro normalizado
 */
export function normalizeResourceParam(param) {
  let id;
  let query;

  if (!isNaN(param)) {
    id = param;
  } else if (param) {
    id = param.id;
    query = param.query;
  }

  return { id, query };
}

/**
 * Notifica sem deixar uma falha na notificação quebrar a cadeia da promise.
 *
 * @param {Function} notify - Notificador de erro
 * @param {any} error - Payload/mensagem de erro
 */
function safeNotify(notify, error) {
  try {
    if (typeof notify === "function") {
      notify(error);
    }
  } catch (notifyError) {
    console.error("Falha ao notificar erro do resource:", notifyError);
  }
}

/**
 * Executa um verbo aplicando o contrato do `apiResource`.
 *
 * @param {Function} handler - Método do service (ou verbo extra da tela)
 * @param {string} verb - Nome do verbo (para mensagem de verbo ausente)
 * @param {Array} args - Argumentos já posicionais do verbo
 * @param {Function} notify - Notificador de erro
 * @returns {Promise<any>} Promise no contrato do `apiResource`
 */
function run(handler, verb, args, notify) {
  return new Promise((resolve, reject) => {
    if (typeof handler !== "function") {
      const missing = {
        error: `Operação "${verb}" não disponível para este recurso.`
      };

      safeNotify(notify, missing);
      reject(missing);
      return;
    }

    Promise.resolve()
      .then(() => handler(...args))
      .then(payload => {
        if (payload && payload.error) {
          safeNotify(notify, payload);
          reject(payload);
        } else {
          resolve(payload);
        }
      })
      .catch(error => {
        safeNotify(notify, error);
        reject(error);
      });
  });
}

/**
 * Cria um resource com a superfície do `apiResource`, delegando aos services.
 *
 * @param {ServiceResourceHandlers} handlers - Verbos service-backed + `notify`
 * @returns {{get: Function, getSilence: Function, save: Function, silentSave: Function, delete: Function, patch: Function}} Resource para o `MasterDetail`
 */
export function createServiceResource(handlers = {}) {
  const { notify, get, getSilence, save, silentSave, remove, patch } = handlers;

  const getHandler = get || getSilence;
  const getSilenceHandler = getSilence || get;
  const saveHandler = save || silentSave;
  const silentSaveHandler = silentSave || save;

  return {
    get: param =>
      run(getHandler, "get", [normalizeResourceParam(param)], notify),
    getSilence: param =>
      run(getSilenceHandler, "getSilence", [normalizeResourceParam(param)], notify),
    save: (data, id) => run(saveHandler, "save", [data, id], notify),
    silentSave: (data, id) =>
      run(silentSaveHandler, "silentSave", [data, id], notify),
    delete: id => run(remove, "delete", [id], notify),
    patch: (data, id) => run(patch, "patch", [data, id], notify)
  };
}

export default createServiceResource;
